import { api } from '../lib/api';

export interface PresignedUpload {
  uploadId: string;
  url: string;
  fields: Record<string, string>;
  expiresAt: string;
}

export interface UploadView {
  id: string;
  entityType: string;
  entityId: string;
  status: 'pending' | 'confirmed';
  createdAt: string;
  confirmedAt: string | null;
}

export interface DownloadUrl {
  url: string;
  expiresAt: string;
}

export function requestUpload(entityType: string, entityId: string, file: File): Promise<PresignedUpload> {
  return api<PresignedUpload>('/uploads', { method: 'POST', body: JSON.stringify({ entityType, entityId, contentType: file.type, sizeBytes: file.size }) });
}

/**
 * Sends the file straight to object storage — never through `api()`, which is same-origin
 * JSON only. Uses `XMLHttpRequest` rather than `fetch` specifically for `xhr.upload.onprogress`;
 * `fetch` has no upload-progress event.
 */
export function putToStorage(presigned: PresignedUpload, file: File, onProgress: (percent: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    Object.entries(presigned.fields).forEach(([key, value]) => form.append(key, value));
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.open('POST', presigned.url);
    xhr.send(form);
  });
}

export function confirmUpload(uploadId: string): Promise<UploadView> {
  return api<UploadView>(`/uploads/${uploadId}/confirm`, { method: 'POST' });
}

export function getUploadDownloadUrl(uploadId: string): Promise<DownloadUrl> {
  return api<DownloadUrl>(`/uploads/${uploadId}/download`);
}
